import type { UseMutationResult } from "@tanstack/react-query"

import {
  isPricingSupersededError,
  PRICING_SUPERSEDED_MESSAGE,
} from "~/api/pricing-errors"

import {
  REFRESH_PRICING_BUSY_LABEL,
  REFRESH_PRICING_ERROR_PROMPT,
  REFRESH_PRICING_LABEL,
} from "./pricing-out-of-date"

/** The parts of a pricing mutation this action reads and drives. */
export type RefreshPricingMutation<TVariables> = Pick<
  UseMutationResult<unknown, unknown, TVariables>,
  `error` | `isError` | `isLoading` | `mutate`
>

/**
 * Where the action sits. `drawer` is the accent button Battery's drawers and
 * Concert form use; `gradient` is the white pill for the investment card's
 * accent gradient; `surface` is the accent button on a plain section panel.
 */
export type RefreshPricingTone = `drawer` | `gradient` | `surface`

const BUTTON_CLASSES: Record<RefreshPricingTone, string> = {
  drawer: `rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover disabled:cursor-not-allowed disabled:opacity-50 print:hidden`,
  gradient: `cursor-pointer rounded-full bg-white px-4 py-1.5 text-[length:var(--step--1)] font-semibold text-bp-accent shadow-[0_8px_24px_rgba(0,0,0,0.2)] hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 print:hidden`,
  surface: `cursor-pointer rounded-md bg-bp-accent px-4 py-2 text-[length:var(--step--1)] font-semibold text-bp-on-accent hover:bg-bp-accent-hover focus-visible:ring-2 focus-visible:ring-bp-accent focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 print:hidden`,
}

const ERROR_CLASSES: Record<RefreshPricingTone, string> = {
  drawer: `text-[length:var(--step--2)] text-white print:hidden`,
  gradient: `order-last basis-full text-[length:var(--step--2)] text-white print:hidden`,
  surface: `text-[length:var(--step--2)] text-bp-danger print:hidden`,
}

/** A pricing command's failure: the superseded explanation, or the refresh prompt. */
export const describePricingError = (error: unknown): string =>
  isPricingSupersededError(error)
    ? PRICING_SUPERSEDED_MESSAGE
    : REFRESH_PRICING_ERROR_PROMPT

/**
 * Refresh error plus the Refresh Pricing button, for any proposal kind. The
 * caller owns the mutation and decides `canEdit` from the server's answer, so
 * the button shows only for an actor the server would let update the proposal.
 * A failure keeps whatever the caller already shows; this only explains it.
 */
export function RefreshPricingAction<TVariables>({
  mutation,
  variables,
  canEdit,
  label = REFRESH_PRICING_LABEL,
  busyLabel = REFRESH_PRICING_BUSY_LABEL,
  describeError = describePricingError,
  tone = `drawer`,
  onRefreshed,
}: {
  mutation: RefreshPricingMutation<TVariables>
  variables: TVariables
  canEdit: boolean
  label?: string
  busyLabel?: string
  /** What the failure says; pricing's copy unless the command is another one. */
  describeError?: (error: unknown) => string
  tone?: RefreshPricingTone
  onRefreshed?: () => void
}): JSX.Element {
  return (
    <>
      {mutation.isError ? (
        <p role="alert" className={ERROR_CLASSES[tone]}>
          {describeError(mutation.error)}
        </p>
      ) : null}
      {canEdit ? (
        <button
          type="button"
          disabled={mutation.isLoading}
          onClick={() => {
            mutation.mutate(variables, { onSuccess: () => onRefreshed?.() })
          }}
          className={BUTTON_CLASSES[tone]}
        >
          {mutation.isLoading ? busyLabel : label}
        </button>
      ) : null}
    </>
  )
}
